import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';

// --- Page & Layout Components ---
import DashboardPage from './component/pages/Dashboard/Dashboard';
import Sidebar from './component/pages/Dashboard/Sidebar';
import Navbar from './component/Navbar/Navbar';
import SettingPageMain from './component/pages/Dashboard/setting/pages/SettingPageMain';
import TaskDetailPage from './component/Task/TaskDetailPage';
import TasksListPage from './component/Task/TasksListPage';
import CreateTaskPage from './component/Task/CreateTaskPage';
import CreateTasksFromTemplatePage from './component/Task/CreateTasksFromTemplatePage.js';
import Project from './component/pages/Dashboard/Project/Project';
import Assets from './component/pages/Dashboard/AssetsModule/Assets';
import Team from './component/Organization/Team';
import TeamManagement from './component/Organization/TeamManagement';
import CommonMaster from '../src/component/Common_master/CommonMaster.js';
import CustomerList from './component/pages/Dashboard/Customer/customer-list.jsx';
import CustomerForm from './component/pages/Dashboard/Customer/form.jsx';
import AdminManager from './component/AdminManager/AdminManager.js';
import FloatingActionButton from './component/common/FloatingActionButton';
import ReportsPage from '../src/component/Report/Report.js';
import ReportSettingsPage from '../src/component/Report/Reportsetting.js';
import ProjectTemplate from './component/ProjectTemplate/ProjectTemplate.js';
import './App.css';

// --- Create and export Contexts ---
export const RefreshContext = createContext();
export const AuthContext = createContext(null);
export const AppLayoutContext = createContext(null); // Context for layout state

// --- API & REDIRECT URLS ---
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const GET_USER_BY_ID_ENDPOINT = `${API_BASE_URL}/api/get-user-by-userId`;
const ZTOOL_LOGIN_REDIRECT_URL = `${process.env.REACT_APP_ZTOOL_URL}/login`;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';


// --- JWT PARSING HELPER ---
const parseJwt = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) { // <-- FIX: Removed the incorrect "=>"
        console.error('Auth Error: Failed to parse JWT.', error);
        return null;
    }
};

// --- HELPER COMPONENTS ---
const RedirectToLogin = ({ message }) => {
    useEffect(() => {
        window.location.replace(ZTOOL_LOGIN_REDIRECT_URL);
    }, []);
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px', textAlign: 'center' }}>
            <h1>{message || 'Redirecting to Login...'}</h1>
        </div>
    );
};

const ZTaskLoginPlaceholder = () => <RedirectToLogin />;


// --- Main Layout Component ---
const MainLayout = () => {
    const [refreshKey, setRefreshKey] = useState(0);
    const { user } = useContext(AuthContext);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // State for mobile sidebar

    const triggerRefresh = () => {
        setRefreshKey((prevKey) => prevKey + 1);
    };
    
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const handleGlobalUpdate = triggerRefresh;

    if (!user) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <h1>Loading User Profile...</h1>
            </div>
        );
    }

    return (
        <RefreshContext.Provider value={{ triggerRefresh }}>
            <AppLayoutContext.Provider value={{ isSidebarOpen, toggleSidebar }}>
                <div className={`app-layout ${isSidebarOpen ? 'sidebar-open' : ''}`}>
                    <Sidebar refreshKey={refreshKey} />
                    <div className="main-content-area">
                        <Navbar />
                        <main className="page-content-wrapper">
                            <Routes>
                                <Route path="/" element={<DashboardPage />} />
                                <Route path="/dashboard" element={<DashboardPage />} />
                                <Route path="/tasks" element={<TasksListPage />} />
                                <Route path="/tasks/create" element={<CreateTaskPage />} />
                                <Route path="/tasks/create-from-template" element={<CreateTasksFromTemplatePage />} />
                                <Route path="/tasks/:taskId" element={<TaskDetailPage />} />
                                <Route path="/assets" element={<Assets />} />
                                <Route path="/project" element={<Project />} />
                                <Route path="/organization/team" element={<Team />} />
                                <Route path="/organization/team-management" element={<TeamManagement />} />
                                <Route path="/organization/user-management" element={<AdminManager onProfileUpdate={handleGlobalUpdate} />} />
                                <Route path="/organization/common-master" element={<CommonMaster />} />
                                <Route path="/organization/project-template" element={<ProjectTemplate />} />
                                <Route path="/customers" element={<CustomerList />} />
                                <Route path="/customers/new" element={<CustomerForm />} />
                                <Route path="/customers/edit/:rowId" element={<CustomerForm />} />
                                <Route path="/settings/*" element={<SettingPageMain onProfileUpdate={handleGlobalUpdate} />} />
                                <Route path="/reports" element={<ReportsPage />} />
                                <Route path="/reports/settings" element={<ReportSettingsPage />} />
                                <Route path="/login" element={<ZTaskLoginPlaceholder />} />
                            </Routes>
                        </main>
                    </div>
                    {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}
                    <FloatingActionButton />
                </div>
            </AppLayoutContext.Provider>
        </RefreshContext.Provider>
    );
};


// --- AUTHENTICATION HANDLER COMPONENT ---
const AuthHandler = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [authStatus, setAuthStatus] = useState('PENDING');
    const [user, setUser] = useState(null);

    useEffect(() => {
        const validateSession = async () => {
            const searchParams = new URLSearchParams(location.search);
            const tokenFromUrl = searchParams.get('token');
            const userDetailsEncodedFromUrl = searchParams.get('userDetails');

            if (tokenFromUrl && userDetailsEncodedFromUrl) {
                localStorage.clear();
                try {
                    const basicUserDetails = JSON.parse(decodeURIComponent(userDetailsEncodedFromUrl));
                    localStorage.setItem(AUTH_TOKEN_KEY, tokenFromUrl);
                    localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(basicUserDetails));
                    navigate(location.pathname, { replace: true, search: '' });
                    await fetchAndSetUserData(basicUserDetails.id, tokenFromUrl);
                } catch (e) {
                    console.error('Auth Error: Failed to process login from URL.', e);
                    localStorage.clear();
                    setAuthStatus('FAILED');
                }
                return;
            }

            const tokenFromLocalStorage = localStorage.getItem(AUTH_TOKEN_KEY);
            const userDetailsString = localStorage.getItem(USER_DETAILS_KEY);
            
            if (tokenFromLocalStorage && userDetailsString) {
                const decodedToken = parseJwt(tokenFromLocalStorage);
                if (decodedToken && decodedToken.exp * 1000 < Date.now()) {
                    localStorage.clear();
                    setAuthStatus('EXPIRED');
                } else {
                    const basicUserDetails = JSON.parse(userDetailsString);
                    await fetchAndSetUserData(basicUserDetails.id, tokenFromLocalStorage);
                }
            } else {
                setAuthStatus('FAILED');
            }
        };

        validateSession();
    }, [location, navigate]);

    const fetchAndSetUserData = async (userId, token) => {
        if (!userId || !token) {
            setAuthStatus('FAILED');
            return;
        }
        try {
            const response = await fetch(`${GET_USER_BY_ID_ENDPOINT}?userId=${userId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Failed to fetch user data.');
            
            const data = await response.json();
            setUser(data.user);
            setAuthStatus('AUTHENTICATED');
        } catch (error) {
            console.error('Auth Error:', error);
            localStorage.clear();
            setAuthStatus('FAILED');
        }
    };
    
    if (authStatus === 'PENDING') {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><h1>Authenticating...</h1></div>
    }
    
    if (authStatus === 'EXPIRED') {
        return <RedirectToLogin message="Your session has expired. Redirecting to login..." />;
    }
    
    if (authStatus === 'FAILED') {
        return <RedirectToLogin />;
    }

    return (
        <AuthContext.Provider value={{ user, setUser }}>
            {children}
        </AuthContext.Provider>
    );
};


// --- MAIN APP COMPONENT ---
const App = () => {
    return (
        <BrowserRouter>
            <AuthHandler>
                <MainLayout />
            </AuthHandler>
        </BrowserRouter>
    );
};

export default App;


